import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Select } from '@/components/ui/Field';
import { databaseApi, type Region } from './api';

const LEVELS = [
  { key: 'province', label: 'Provinsi' },
  { key: 'city', label: 'Kabupaten' },
  { key: 'village', label: 'Desa/Nagari' },
] as const;

/**
 * Pemilih wilayah berjenjang: Provinsi → Kabupaten → Desa.
 *
 * Wilayah sengaja berupa data referensi, bukan teks bebas: begitu diketik
 * manual, "Kab Solok"/"Kabupaten Solok"/"solok" akan jadi tiga wilayah berbeda
 * dan agregasi dashboard per wilayah langsung pecah.
 *
 * Yang disimpan HANYA id desa. Provinsi & kabupatennya ditelusuri lewat
 * parent_id, tidak ikut disalin — kalau disalin, keduanya bisa berbeda dari
 * desanya dan tidak ada cara menentukan mana yang benar.
 */
export function RegionPicker({
  value,
  onChange,
  error,
  required,
}: {
  value: string | null;
  onChange: (regionId: string | null) => void;
  error?: string;
  required?: boolean;
}) {
  const [chain, setChain] = useState<(string | null)[]>([null, null, null]);

  // Saat menyunting data lama, rantai dipulihkan dari jalur wilayahnya.
  const { data: path } = useQuery({
    queryKey: ['regionPath', value],
    queryFn: () => api.get<Region[]>(`/regions/${value}/path`),
    enabled: Boolean(value),
  });

  useEffect(() => {
    if (!path) return;
    const next: (string | null)[] = [null, null, null];
    path.forEach((r) => {
      const idx = LEVELS.findIndex((l) => l.key === r.level);
      if (idx >= 0) next[idx] = r.id;
    });
    setChain(next);
  }, [path]);

  const select = (levelIndex: number, regionId: string | null) => {
    // Memilih ulang level atas membatalkan level di bawahnya, supaya tidak
    // tertinggal kombinasi yang tidak masuk akal.
    const next = chain.map((v, i) => (i < levelIndex ? v : i === levelIndex ? regionId : null));
    setChain(next);

    // Hanya desa yang dilaporkan ke form. Provinsi atau kabupaten saja tidak
    // cukup: kolomnya menunjuk tingkat desa, dan menyimpan tingkat lain akan
    // membuat penelusuran wilayah menghasilkan jalur yang tidak lengkap.
    onChange(next[LEVELS.length - 1] ?? null);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {LEVELS.map((level, i) => (
          <RegionLevelSelect
            key={level.key}
            label={level.label}
            level={level.key}
            parentId={i === 0 ? null : (chain[i - 1] ?? null)}
            value={chain[i] ?? ''}
            onChange={(id) => select(i, id)}
            disabled={i > 0 && !chain[i - 1]}
            required={required && i === LEVELS.length - 1}
          />
        ))}
      </div>
      {error && <p className="text-[13px] text-danger">{error}</p>}
      <p className="text-[13px] text-ink-muted">
        Isi sampai tingkat desa — itu yang disimpan sebagai lokasi TBM.
      </p>
    </div>
  );
}

function RegionLevelSelect({
  label,
  level,
  parentId,
  value,
  onChange,
  disabled,
  required,
}: {
  label: string;
  level: string;
  parentId: string | null;
  value: string;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  required?: boolean;
}) {
  const enabled = !disabled;

  const { data, isLoading } = useQuery({
    // Level ikut jadi kunci: tanpa itu, level anak yang induknya belum dipilih
    // memakai parentId null dan berbagi cache dengan daftar provinsi —
    // akibatnya semua dropdown menampilkan daftar provinsi.
    queryKey: ['regions', level, parentId],
    queryFn: () => databaseApi.regions({ parentId }),
    enabled,
  });

  // Selama induk belum dipilih, tidak ada pilihan yang sah untuk ditawarkan.
  const options = enabled ? (data ?? []) : [];

  return (
    <Select
      label={label}
      value={value}
      required={required}
      disabled={disabled || isLoading}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">
        {disabled
          ? '— pilih tingkat di atasnya dulu —'
          : isLoading
            ? 'Memuat…'
            : options.length === 0
              ? '— belum ada data —'
              : `Pilih ${label}`}
      </option>
      {options.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
    </Select>
  );
}
